import ApiService from '../../../services/ApiService';
import Logger from '../../../services/Logger';
import { getContentLanguage } from '../../../services/LangPrefs';

const CrossService = {
    getCountPublished: async (type = 'CROSSWORD') => {
        try {
            const params = new URLSearchParams({ type });
            const data = await ApiService.getData(`/home/cross/countPublished?${params.toString()}`);
            return data || { english: { free: 0, paid: 0 }, french: { free: 0, paid: 0 } };
        } catch (error) {
            Logger.error('Error fetching cross count published:', error);
            return { english: { free: 0, paid: 0 }, french: { free: 0, paid: 0 } };
        }
    },

    requestFreePuzzles: async (type = 'CROSSWORD', limit = 2, tagOrTags) => {
        try {
            const language = getContentLanguage();
            let url = `/home/cross?free=true&limit=${limit}&type=${type}&sort=publishedAt:desc&language=${language}`;
            const tags = Array.isArray(tagOrTags) ? tagOrTags : (tagOrTags ? [tagOrTags] : []);
            tags.forEach((tag) => { url += `&tags=${encodeURIComponent(tag)}`; });
            const puzzles = await ApiService.getData(url);
            return puzzles;
        } catch (error) {
            Logger.error('Error fetching free puzzles:', error);
            return [];
        }
    },

    /**
     * @param {'all'|'free'|'paid'} [accessFilter] - free = free puzzles only; paid = premium only; all = no filter
     * @param {boolean} [includeLanguage=true] - set false for account library tabs (show all languages)
     */
    requestPlayablePuzzles: async (
        type,
        page = 0,
        pageSize = 10,
        archived,
        isCompleted,
        tagOrTags,
        accessFilter = 'all',
        includeLanguage = true
    ) => {
        try {
            const params = new URLSearchParams({
                page,
                pageSize,
                sort: 'publishedAt:desc',
            });
            if (includeLanguage) {
                params.append('language', getContentLanguage());
            }

            // when archived or isCompleted is undefined, don't append to the URL
            if (archived !== undefined) params.append('archived', archived);
            if (isCompleted !== undefined) params.append('isCompleted', isCompleted);

            if (type) params.append('type', type); // only include if defined
            const tags = Array.isArray(tagOrTags) ? tagOrTags : (tagOrTags ? [tagOrTags] : []);
            tags.forEach((tag) => params.append('tags', tag));

            if (accessFilter === 'free') {
                params.append('free', 'true');
            } else if (accessFilter === 'paid') {
                params.append('free', 'false');
            }

            const response = await ApiService.getData(`/home/cross/paging?${params.toString()}`);
            return response;
        } catch (error) {
            Logger.error('Error fetching all puzzles:', error);
            return { content: [], totalElements: 0, totalPages: 0 };
        }
    },

    getPuzzleById: async (id, options = {}) => {
        try {
            const params = new URLSearchParams();
            if (options.isStory) params.set("isStory", "true");
            const qs = params.toString();
            const suffix = qs ? `?${qs}` : "";
            const data = await ApiService.getData(`/home/cross/${id}${suffix}`);

            // Return the raw API data directly
            return data;
        } catch (error) {
            Logger.error('Error fetching puzzle by ID:', error);
            return null;
        }
    },

    startPlay: async (crossId) => {
        try {
            let params = {
                crossId,
            };
            const data = await ApiService.postData(`/home/cross-play/start`, params);
            return data;
        } catch (error) {
            Logger.error('Error starting crossword play:', error);
            return null;
        }
    },

    restartPlay: async (crossId) => {
        try {
            let params = {
                crossId,
            };
            const data = await ApiService.postData(`/home/cross-play/restart`, params);
            return data;
        } catch (error) {
            Logger.error('Error restarting crossword play:', error);
            return null;
        }
    },

    progressPuzzle: async (_cloudUserId, crossId, wordsFound) => {
        try {
            let params = {
                crossId,
                foundWords: wordsFound  // Map wordsFound to foundWords for API compatibility
            };
            const data = await ApiService.putData(`/home/cross-play/progress`, params);
            return data;
        } catch (error) {
            Logger.error('Error updating puzzle progress:', error);
            return null;
        }
    },

    /**
     * Story crosswords: notify backend when every word in the puzzle has been found.
     * @param {string} crossId
     * @param {boolean} [isCompleteStory=true]
     */
    completeStory: async (crossId, isCompleteStory = true) => {
        try {
            const params = { crossId, isCompleteStory };
            const data = await ApiService.putData(`/home/cross-play/complete-story`, params);
            return data;
        } catch (error) {
            Logger.error('Error recording story completion:', error);
            return null;
        }
    },

    /**
     * Record word(s) solved while crossword hints were active.
     * @param {string} crossId - template / play cross UUID
     * @param {{ word?: string, words?: string[] }} payload
     */
    hintUsed: async (crossId, { word, words } = {}) => {
        try {
            const list = [];
            if (Array.isArray(words)) {
                for (const w of words) {
                    const t = String(w ?? "").trim();
                    if (t) list.push(t);
                }
            }
            if (word != null && String(word).trim()) {
                list.push(String(word).trim());
            }
            if (!list.length) {
                Logger.warn("CrossService.hintUsed: no word(s) to send");
                return null;
            }
            const params = { crossId };
            if (list.length === 1) {
                params.word = list[0];
            } else {
                params.words = list;
            }
            const data = await ApiService.putData(`/home/cross-play/hint-used`, params);
            return data;
        } catch (error) {
            Logger.error('Error recording hint usage:', error);
            return null;
        }
    },

    /**
     * Get words with meanings for a crossword puzzle. Uses template ID (cross Tpl ID).
     * @param {string} crossTplId - Cross template ID (UUID)
     * @returns {Promise<Array>} Array of { number, word, direction, meaning: string[] }
     */
    getWordsWithMeanings: async (crossTplId) => {
        try {
            if (!crossTplId) return [];
            const data = await ApiService.getData(`/home/cross-play/${crossTplId}/words-with-meanings`);
            if (Array.isArray(data)) return data;
            if (data && Array.isArray(data.words)) return data.words;
            if (data && Array.isArray(data.content)) return data.content;
            if (data && Array.isArray(data.items)) return data.items;
            return [];
        } catch (error) {
            Logger.error('Error fetching words with meanings:', error);
            return [];
        }
    },

    /**
     * Record PDF download for analytics. Uses cross template ID.
     * @param {string} crossTplId - Cross template ID (UUID)
     * @param {string} format - 'letter' or 'a4'
     * @param {boolean} [useDisplayClue=true] - Sent as query param when true
     */
    recordDownloadPdf: async (crossTplId, format = 'letter', useDisplayClue = true) => {
        try {
            const params = useDisplayClue ? { format, useDisplayClue: true } : { format };
            await ApiService.getData(`/home/cross-play/${crossTplId}/record-download-pdf`, params);
        } catch (error) {
            Logger.error('Error recording PDF download:', error);
        }
    },

    /**
     * Record download and then fetch/download PDF. Uses pdfUrl if provided, otherwise calls REST API.
     * @param {string} templateId - Template ID for API download and record
     * @param {string} format - 'letter' or 'a4'
     * @param {{ pdfUrl?: string, title?: string, useDisplayClue?: boolean }} options - Optional pre-generated pdfUrl and filename
     */
    recordAndDownloadPdf: async (templateId, format = 'letter', { pdfUrl, title, useDisplayClue = true } = {}) => {
        if (templateId) {
            await CrossService.recordDownloadPdf(templateId, format, useDisplayClue);
        }


        const link = document.createElement('a');
        link.download = `${title || 'puzzle'}.pdf`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        if (pdfUrl) {
            link.href = pdfUrl;
        } else {
            const queryParams = useDisplayClue ? { format, useDisplayClue: true } : { format };
            const blob = await ApiService.downloadFile(`/home/cross-play/${templateId}/download-pdf`, queryParams);
            link.href = window.URL.createObjectURL(blob);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);
            return;
        }
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
};

export default CrossService;

